package org.example.project.client.api

import io.ktor.client.HttpClient
import io.ktor.client.plugins.HttpRequestTimeoutException
import io.ktor.client.request.header
import io.ktor.client.request.request
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.HttpMethod
import io.ktor.http.HttpStatusCode
import io.ktor.http.contentType
import io.ktor.http.encodeURLParameter
import kotlinx.coroutines.CancellationException
import kotlinx.serialization.SerializationException
import kotlinx.serialization.json.Json
import org.example.project.client.config.ApiConfig
import org.example.project.client.error.ConflictException
import org.example.project.client.error.ForbiddenException
import org.example.project.client.error.MissingCsrfTokenException
import org.example.project.client.error.NetworkException
import org.example.project.client.error.RateLimitedException
import org.example.project.client.error.RequestTimeoutException
import org.example.project.client.error.ServerErrorException
import org.example.project.client.error.UnauthorizedException
import org.example.project.client.error.UnexpectedPayloadException
import org.example.project.client.error.ValidationException
import org.example.project.client.model.ActivityFilters
import org.example.project.client.model.ActivityListResponse
import org.example.project.client.model.ActivityResponse
import org.example.project.client.model.ApiErrorResponse
import org.example.project.client.model.CreateActivityRequest
import org.example.project.client.model.UpdateActivityRequest
import org.example.project.client.session.SessionRepository
import org.example.project.client.session.SessionState

interface ActivityApiClient {
    suspend fun listActivities(filters: ActivityFilters = ActivityFilters()): ActivityListResponse
    suspend fun getActivity(id: String): ActivityResponse
    suspend fun createDraft(
        request: CreateActivityRequest,
        bearerToken: String? = null,
        csrfToken: String? = null,
        isWeb: Boolean = false
    ): ActivityResponse
    suspend fun updateActivity(
        id: String,
        request: UpdateActivityRequest,
        bearerToken: String? = null,
        csrfToken: String? = null,
        isWeb: Boolean = false
    ): ActivityResponse
    suspend fun publishActivity(
        id: String,
        bearerToken: String? = null,
        csrfToken: String? = null,
        isWeb: Boolean = false
    ): ActivityResponse
    suspend fun getManagedActivity(
        id: String,
        bearerToken: String? = null,
        csrfToken: String? = null,
        isWeb: Boolean = false
    ): ActivityResponse
    suspend fun listManagedActivities(
        search: String? = null,
        status: String? = null,
        sort: String? = null,
        limit: Int? = null,
        offset: Int? = null,
        bearerToken: String? = null,
        csrfToken: String? = null,
        isWeb: Boolean = false
    ): ActivityListResponse
    fun close()
}

class DefaultActivityApiClient(
    private val httpClient: HttpClient,
    private val apiConfig: ApiConfig,
    private val json: Json = Json {
        ignoreUnknownKeys = true
        isLenient = false
    },
    private val ownsHttpClient: Boolean = false
) : ActivityApiClient {

    override suspend fun listActivities(filters: ActivityFilters): ActivityListResponse {
        val queryParts = mutableListOf<String>()
        filters.search?.trim()?.takeIf { it.isNotEmpty() }?.let {
            queryParts.add("search=${it.encodeURLParameter()}")
        }
        filters.age?.let { queryParts.add("age=$it") }
        filters.minAge?.let { queryParts.add("minAge=$it") }
        filters.maxAge?.let { queryParts.add("maxAge=$it") }
        filters.category?.trim()?.takeIf { it.isNotEmpty() }?.let {
            queryParts.add("category=${it.encodeURLParameter()}")
        }
        filters.minDuration?.let { queryParts.add("minDuration=$it") }
        filters.maxDuration?.let { queryParts.add("maxDuration=$it") }
        filters.sort?.trim()?.takeIf { it.isNotEmpty() }?.let {
            queryParts.add("sort=${it.encodeURLParameter()}")
        }
        filters.limit?.let { queryParts.add("limit=$it") }
        filters.offset?.let { queryParts.add("offset=$it") }

        val queryString = if (queryParts.isNotEmpty()) "?" + queryParts.joinToString("&") else ""
        val url = apiConfig.resolve("/activities") + queryString

        val response = executeRequest(url = url, method = HttpMethod.Get)
        return decodeBody(response)
    }

    override suspend fun getActivity(id: String): ActivityResponse {
        val cleanId = id.trim()
        val url = apiConfig.resolve("/activities/$cleanId")
        val response = executeRequest(url = url, method = HttpMethod.Get)
        return decodeBody(response)
    }

    override suspend fun createDraft(
        request: CreateActivityRequest,
        bearerToken: String?,
        csrfToken: String?,
        isWeb: Boolean
    ): ActivityResponse {
        if (isWeb && csrfToken.isNullOrBlank()) {
            throw MissingCsrfTokenException("Missing CSRF token for web activity creation")
        }
        val url = apiConfig.resolve("/activities")
        val headers = mutableMapOf<String, String>()
        if (!bearerToken.isNullOrBlank()) {
            headers[HttpHeaders.Authorization] = "Bearer $bearerToken"
        }
        if (isWeb && !csrfToken.isNullOrBlank()) {
            headers["X-CSRF-Token"] = csrfToken
        }
        val response = executeRequest(
            url = url,
            method = HttpMethod.Post,
            headers = headers,
            body = request
        )
        return decodeBody(response)
    }

    override suspend fun updateActivity(
        id: String,
        request: UpdateActivityRequest,
        bearerToken: String?,
        csrfToken: String?,
        isWeb: Boolean
    ): ActivityResponse {
        if (isWeb && csrfToken.isNullOrBlank()) {
            throw MissingCsrfTokenException("Missing CSRF token for web activity update")
        }
        val cleanId = id.trim()
        val url = apiConfig.resolve("/activities/$cleanId")
        val headers = mutableMapOf<String, String>()
        if (!bearerToken.isNullOrBlank()) {
            headers[HttpHeaders.Authorization] = "Bearer $bearerToken"
        }
        if (isWeb && !csrfToken.isNullOrBlank()) {
            headers["X-CSRF-Token"] = csrfToken
        }
        val response = executeRequest(
            url = url,
            method = HttpMethod.Put,
            headers = headers,
            body = request
        )
        return decodeBody(response)
    }

    override suspend fun publishActivity(
        id: String,
        bearerToken: String?,
        csrfToken: String?,
        isWeb: Boolean
    ): ActivityResponse {
        if (isWeb && csrfToken.isNullOrBlank()) {
            throw MissingCsrfTokenException("Missing CSRF token for web activity publish")
        }
        val cleanId = id.trim()
        val url = apiConfig.resolve("/activities/$cleanId/publish")
        val headers = mutableMapOf<String, String>()
        if (!bearerToken.isNullOrBlank()) {
            headers[HttpHeaders.Authorization] = "Bearer $bearerToken"
        }
        if (isWeb && !csrfToken.isNullOrBlank()) {
            headers["X-CSRF-Token"] = csrfToken
        }
        val response = executeRequest(
            url = url,
            method = HttpMethod.Post,
            headers = headers
        )
        return decodeBody(response)
    }

    override suspend fun getManagedActivity(
        id: String,
        bearerToken: String?,
        csrfToken: String?,
        isWeb: Boolean
    ): ActivityResponse {
        val cleanId = id.trim()
        val url = apiConfig.resolve("/activities/manage/$cleanId")
        val headers = mutableMapOf<String, String>()
        if (!bearerToken.isNullOrBlank()) {
            headers[HttpHeaders.Authorization] = "Bearer $bearerToken"
        }
        if (isWeb && !csrfToken.isNullOrBlank()) {
            headers["X-CSRF-Token"] = csrfToken
        }
        val response = executeRequest(
            url = url,
            method = HttpMethod.Get,
            headers = headers
        )
        return decodeBody(response)
    }

    override suspend fun listManagedActivities(
        search: String?,
        status: String?,
        sort: String?,
        limit: Int?,
        offset: Int?,
        bearerToken: String?,
        csrfToken: String?,
        isWeb: Boolean
    ): ActivityListResponse {
        val queryParts = mutableListOf<String>()
        search?.trim()?.takeIf { it.isNotEmpty() }?.let {
            queryParts.add("search=${it.encodeURLParameter()}")
        }
        status?.trim()?.takeIf { it.isNotEmpty() }?.let {
            queryParts.add("status=${it.encodeURLParameter()}")
        }
        sort?.trim()?.takeIf { it.isNotEmpty() }?.let {
            queryParts.add("sort=${it.encodeURLParameter()}")
        }
        limit?.let { queryParts.add("limit=$it") }
        offset?.let { queryParts.add("offset=$it") }

        val queryString = if (queryParts.isNotEmpty()) "?" + queryParts.joinToString("&") else ""
        val url = apiConfig.resolve("/activities/manage") + queryString

        val headers = mutableMapOf<String, String>()
        if (!bearerToken.isNullOrBlank()) {
            headers[HttpHeaders.Authorization] = "Bearer $bearerToken"
        }
        if (isWeb && !csrfToken.isNullOrBlank()) {
            headers["X-CSRF-Token"] = csrfToken
        }

        val response = executeRequest(
            url = url,
            method = HttpMethod.Get,
            headers = headers
        )
        return decodeBody(response)
    }

    private suspend fun executeRequest(
        url: String,
        method: HttpMethod,
        headers: Map<String, String> = emptyMap(),
        body: Any? = null
    ): HttpResponse {
        require(apiConfig.isSameOrigin(url)) {
            "Request URL violates origin boundary of configured API base URL"
        }

        val httpResponse: HttpResponse = try {
            httpClient.request(url) {
                this.method = method
                if (body != null) {
                    contentType(ContentType.Application.Json)
                    setBody(body)
                }
                headers.forEach { (name, value) ->
                    header(name, value)
                }
            }
        } catch (e: CancellationException) {
            throw e
        } catch (_: HttpRequestTimeoutException) {
            throw RequestTimeoutException()
        } catch (e: Exception) {
            val msg = e.message ?: ""
            if (msg.contains("timeout", ignoreCase = true)) {
                throw RequestTimeoutException()
            }
            throw NetworkException()
        }

        checkStatusAndThrow(httpResponse)
        return httpResponse
    }

    private suspend fun checkStatusAndThrow(response: HttpResponse) {
        val status = response.status
        if (status.value in 200..299) {
            return
        }

        val requestIdHeader = response.headers[HttpHeaders.XRequestId] ?: "unknown"
        val bodyText = try {
            response.bodyAsText()
        } catch (e: CancellationException) {
            throw e
        } catch (_: Exception) {
            ""
        }

        val errorResponse = try {
            if (bodyText.isNotBlank()) json.decodeFromString<ApiErrorResponse>(bodyText) else null
        } catch (_: Exception) {
            null
        }

        val errorCode = errorResponse?.error ?: when (status) {
            HttpStatusCode.BadRequest -> "BAD_REQUEST"
            HttpStatusCode.Unauthorized -> "UNAUTHORIZED"
            HttpStatusCode.Forbidden -> "FORBIDDEN"
            HttpStatusCode.NotFound -> "NOT_FOUND"
            HttpStatusCode.Conflict -> "CONFLICT"
            HttpStatusCode.TooManyRequests -> "TOO_MANY_REQUESTS"
            else -> "HTTP_${status.value}"
        }
        val errorMessage = errorResponse?.message ?: "HTTP ${status.value}"
        val reqId = errorResponse?.requestId ?: requestIdHeader

        when (status) {
            HttpStatusCode.BadRequest, HttpStatusCode.NotFound -> {
                throw ValidationException(status.value, errorCode, errorMessage, reqId)
            }
            HttpStatusCode.Unauthorized -> throw UnauthorizedException(status.value, errorCode, errorMessage, reqId)
            HttpStatusCode.Forbidden -> throw ForbiddenException(status.value, errorCode, errorMessage, reqId)
            HttpStatusCode.Conflict -> throw ConflictException(status.value, errorCode, errorMessage, reqId)
            HttpStatusCode.TooManyRequests -> {
                val retryAfter = response.headers[HttpHeaders.RetryAfter]?.toLongOrNull()
                throw RateLimitedException(status.value, errorCode, errorMessage, reqId, retryAfter)
            }
            else -> {
                if (status.value >= 500) {
                    throw ServerErrorException(status.value, errorCode, errorMessage, reqId)
                }
                throw ValidationException(status.value, errorCode, errorMessage, reqId)
            }
        }
    }

    private suspend inline fun <reified T> decodeBody(response: HttpResponse): T {
        val text = try {
            response.bodyAsText()
        } catch (e: CancellationException) {
            throw e
        } catch (_: Exception) {
            throw UnexpectedPayloadException()
        }

        return try {
            json.decodeFromString<T>(text)
        } catch (_: SerializationException) {
            throw UnexpectedPayloadException()
        }
    }

    override fun close() {
        if (ownsHttpClient) {
            httpClient.close()
        }
    }
}

suspend fun ActivityApiClient.createDraftWithSession(
    request: CreateActivityRequest,
    sessionRepository: SessionRepository
): ActivityResponse {
    return when (val state = sessionRepository.sessionState.value) {
        is SessionState.AuthenticatedAndroid -> createDraft(request, bearerToken = state.token, isWeb = false)
        is SessionState.AuthenticatedWeb -> {
            if (state.reauthenticationRequiredForMutation || state.csrfToken == null) {
                throw MissingCsrfTokenException()
            }
            createDraft(request, csrfToken = state.csrfToken, isWeb = true)
        }
        is SessionState.Unauthenticated -> throw UnauthorizedException(401, "UNAUTHORIZED", "Authentication required", "unknown")
    }
}

suspend fun ActivityApiClient.updateActivityWithSession(
    id: String,
    request: UpdateActivityRequest,
    sessionRepository: SessionRepository
): ActivityResponse {
    return when (val state = sessionRepository.sessionState.value) {
        is SessionState.AuthenticatedAndroid -> updateActivity(id, request, bearerToken = state.token, isWeb = false)
        is SessionState.AuthenticatedWeb -> {
            if (state.reauthenticationRequiredForMutation || state.csrfToken == null) {
                throw MissingCsrfTokenException()
            }
            updateActivity(id, request, csrfToken = state.csrfToken, isWeb = true)
        }
        is SessionState.Unauthenticated -> throw UnauthorizedException(401, "UNAUTHORIZED", "Authentication required", "unknown")
    }
}

suspend fun ActivityApiClient.publishActivityWithSession(
    id: String,
    sessionRepository: SessionRepository
): ActivityResponse {
    return when (val state = sessionRepository.sessionState.value) {
        is SessionState.AuthenticatedAndroid -> publishActivity(id, bearerToken = state.token, isWeb = false)
        is SessionState.AuthenticatedWeb -> {
            if (state.reauthenticationRequiredForMutation || state.csrfToken == null) {
                throw MissingCsrfTokenException()
            }
            publishActivity(id, csrfToken = state.csrfToken, isWeb = true)
        }
        is SessionState.Unauthenticated -> throw UnauthorizedException(401, "UNAUTHORIZED", "Authentication required", "unknown")
    }
}

suspend fun ActivityApiClient.listManagedActivitiesWithSession(
    search: String? = null,
    status: String? = null,
    sort: String? = null,
    limit: Int? = null,
    offset: Int? = null,
    sessionRepository: SessionRepository
): ActivityListResponse {
    return when (val state = sessionRepository.sessionState.value) {
        is SessionState.AuthenticatedAndroid -> listManagedActivities(
            search = search,
            status = status,
            sort = sort,
            limit = limit,
            offset = offset,
            bearerToken = state.token,
            isWeb = false
        )
        is SessionState.AuthenticatedWeb -> listManagedActivities(
            search = search,
            status = status,
            sort = sort,
            limit = limit,
            offset = offset,
            csrfToken = state.csrfToken,
            isWeb = true
        )
        is SessionState.Unauthenticated -> throw UnauthorizedException(401, "UNAUTHORIZED", "Authentication required", "unknown")
    }
}

